import {
    AfterContentInit, AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {IQuotationCost} from 'libraries/service/resources/quotation-quotations/IQuotationsQuotation';
import {INumberFieldSpecs} from 'libraries/module/formbuilder/fields/number/INumberFieldSpecs';
import {EMoveMode} from "../../../enums/e-move-mode";

@Component({
    selector: 'td-text',
    template: `
        <ng-template #template>
            <td *ngIf="!isInEdition"
                #cell
                [attr.colspan]="colspan"
                [class]="parity"
                [class.odd]="isOdd"
                [class.even]="!isOdd"
                [class.white]="isWhite"
                [class.no-border]="noBorder"
                [class.border]="border"
                [class.has-click-action]="hasClickAction"
                [class.align-right]="alignRight"
                [class.align-center]="alignCenter"
                [class.no-wrap]="noWrap"
                [class.is-top]="isTop"
                [class.is-light-color]="isLightColor"
                [class.small-height]="isSmallHeight"
                [class.is-hidden]="isHidden"
                (click)="hasClickAction ? onClick.emit(): null">
                <div class="additional-info cell-header" *ngIf="header">{{header}}</div>
                {{text}}
                <ng-content class="additional-content"></ng-content>
                <ng-content class="full-content"></ng-content>
                <div class="additional-info cell-footer" *ngIf="footer">{{footer}}</div>
                <div class="td-footer"></div>
            </td>
        </ng-template>
    `,
    styles: [`
        :host {
            display: none
        }

        td {
            font-size: 1rem;
            color: #414141;
            padding: .75rem;
            font-weight: normal;
            text-align: left;
            border-right: 1px solid white;
            border-bottom: 1px solid white;
            position: relative;
        }

        td.no-border {
            border: 0;
        }

        td.border {
            border: 1px solid white !important;
        }
        
        td.odd {
            background: #ecedef;
        }

        td.even {
            background: #f1f1f1;
        }

        td.odd.is-light-color {
            background: white;
        }

        td.even.is-light-color {
            background: #f9fafc;
        }

        td.white {
            background: white;
        }


        td.is-top {
            vertical-align: top;
        }

        td.align-right {
            text-align: right;
        }

        td.align-center {
            text-align: center;
        }

        td.has-click-action {
            cursor: pointer;
            user-select: none;
        }

        td.small-height {
            padding: .375rem .75rem;
        }

        .additional-info {
            position: absolute;
            opacity: 0.60;
            font-size: .75em;
            white-space: nowrap;
        }

        .cell-footer {
            bottom: .1rem;
            right: 0.75rem;
        }

        .cell-header {
            top: .15rem;
            left: 0.75rem;
        }

        .no-wrap {
            white-space: nowrap;
        }

        .additional-content {
            float: right;
        }

        .is-hidden {
            display: none;
            
        }

    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class TdTextComponent implements OnInit {
    @Input()
    public text: string;
    @Input()
    public parity: "odd" | "even" = "odd";
    @Input()
    public isSticky: boolean;
    @Input()
    public leftPosition: number;
    @Input()
    public canHaveFooter: boolean = true;
    @Input()
    public noBorder: boolean;
    @Input()
    public border: boolean;
    @Input()
    public strong: boolean;
    @Input()
    public canEmitRightPosition: boolean;
    @Input()
    public minWidth: string;
    @Input()
    public isOdd: boolean;
    @Input()
    public isWhite: boolean;
    @Input()
    public noWrap: boolean;
    @Input()
    public header: string;
    @Input()
    public footer: string;
    @Input()
    public alignRight: boolean;
    @Input()
    public alignCenter: boolean;
    @Input()
    public hasClickAction: boolean;
    @Input()
    public isTop: boolean;
    @Input()
    public isSmallHeight: boolean;
    @Input()
    public isLightColor: boolean;
    @Input()
    public colspan: number = 1;
    @Input()
    public isHidden: boolean;


    /**
     * Indicate that Edition is opened from keyboard navigation  in parent component
     */
    @Input()
    public isKeyboardOpen: boolean;

    @Output()
    public readonly onRightPositionChange: EventEmitter<number> = new EventEmitter<number>();
    @Output()
    public readonly onFocusField: EventEmitter<null> = new EventEmitter<null>();
    @Output()
    public readonly onPriceChange: EventEmitter<IQuotationCost> = new EventEmitter<IQuotationCost>();
    @Output()
    public readonly onMoveModeChange: EventEmitter<EMoveMode> = new EventEmitter<EMoveMode>();
    @Output()
    public readonly onClick: EventEmitter<void> = new EventEmitter<void>();

    public forceFocus: boolean = false;
    public isBlurOnInput: boolean = false;
    public fieldConfig: INumberFieldSpecs;
    public isInEdition: boolean = false;

    @ViewChild('template', {read: TemplateRef, static: true})
    public template: TemplateRef<{}>;
    @ViewChild('cell')
    public cell: ElementRef;

    constructor(
        private readonly viewContainerRef: ViewContainerRef,
    ) {
    }

    public ngOnInit(): void {
        this.viewContainerRef.createEmbeddedView(this.template);
        setTimeout(() => this.stickCell());
    }


    public fixWidth(minWidth?: string): void {
        if (minWidth) {
            this.minWidth = minWidth;
        }
    }

    public emitRightPosition(position: number): void {
        if (this.canEmitRightPosition) {
            this.onRightPositionChange.emit(position);
        }
    }

    private stickCell(): void {
        //dynamic sticky, this way we don't need any extra css
        if (this.isSticky) {
            const left: number = this.cell.nativeElement.offsetLeft - this.cell.nativeElement.parentElement.offsetParent.offsetLeft;
            this.cell.nativeElement.style.zIndex = "1";
            this.cell.nativeElement.style.left = `${left}px`;
            this.cell.nativeElement.style.position = "sticky";
        }
    }

}
